import React from 'react';
import moment from 'moment-timezone';
import { useAsyncData } from '@shared/react';
import { Preloader, ChangedValueItem } from '@shared/UI';
import { getAffectedPositions } from './api';
import { useErrorNotify } from '@features/useErrorNotify';
import { MdOutlineWaterDrop } from 'react-icons/md';
import { GoPlus } from 'react-icons/go';
import { LuMinus } from 'react-icons/lu';
import { IoCloseOutline } from 'react-icons/io5';
import './AffectedPositions.scss';
import classNames from 'classnames';
import { Tooltip } from 'react-tooltip';
import { formats } from '@shared/utils';

interface IAffectedPositionsProps {
    transactionId: number;
}

export const AffectedPositions: React.FC<IAffectedPositionsProps> = ({ transactionId }) => {
    const {
        data: affectedPositions,
        loading,
        error,
    } = useAsyncData(
        async () => {
            return await getAffectedPositions({ transactionId });
        },
        null,
        [],
    );

    useErrorNotify(error, 'Affected Positions');

    const termStatuses = {
        Undetermined: 'Undetermined-Term',
        ShortTerm: 'Short-Term',
        LongTerm: 'Long-Term',
    };

    return (
        <Preloader loading={loading}>
            <div className='affected-positions'>
                {affectedPositions?.opened && (
                    <div className='affected-positions__position'>
                        <div className='title'>
                            <GoPlus /> <h3>Opened Positions</h3>
                        </div>
                        {affectedPositions.opened.map((position) => (
                            <div className='position-data'>
                                <div className='position-data__info'>
                                    <div className='position-data__info__item'>
                                        <div className='position-data__info__item__title'>
                                            <span className='position-data__info__item__value title'>
                                                {position.id}
                                            </span>
                                            <div className='position-data__tag term'>
                                                <div
                                                    className={classNames('circle', {
                                                        long: position.term === 'LongTerm',
                                                        short: position.term === 'ShortTerm',
                                                        undetermined: position.term === 'Undetermined',
                                                    })}
                                                />
                                                {termStatuses[position.term]}
                                            </div>
                                            {Boolean(position.washSale) && (
                                                <>
                                                    <div
                                                        data-tooltip-id='washSale'
                                                        className='position-data__tag drop'
                                                    >
                                                        <MdOutlineWaterDrop />
                                                    </div>
                                                    <Tooltip
                                                        id={'washSale'}
                                                        place={'right'}
                                                        content={'Wash Sale'}
                                                        opacity={1}
                                                        style={{
                                                            backgroundColor: '#FFFFFF',
                                                            padding: 12,
                                                            borderRadius: 12,
                                                            fontSize: 12,
                                                            color: '#000000BF',
                                                            boxShadow: '0px 0px 6px -1px #0000001A',
                                                            maxWidth: '500px',
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className='position-data__info__item'>
                                        <span className='position-data__info__item__key'>Quantity:</span>
                                        <ChangedValueItem
                                            oldValue={position.oldQty}
                                            newValue={position.newQty}
                                            change={position.change}
                                        />
                                    </div>
                                    <div className='position-data__info__item'>
                                        <span className='position-data__info__item__key'>Date:</span>
                                        <span className='position-data__info__item__value'>
                                            {moment(position.executionDate).format(formats.fullTime)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {affectedPositions?.modified && (
                    <div className='affected-positions__position'>
                        <div className='title'>
                            <LuMinus /> <h3>Reducing Positions</h3>
                        </div>

                        {affectedPositions.modified.map((position) => (
                            <div className='position-data'>
                                <div className='position-data__info'>
                                    <div className='position-data__info__item'>
                                        <div className='position-data__info__item__title'>
                                            <span className='position-data__info__item__value title'>
                                                {position.id}
                                            </span>
                                            <div className='position-data__tag term'>
                                                <div
                                                    className={classNames('circle', {
                                                        long: position.term === 'LongTerm',
                                                        short: position.term === 'ShortTerm',
                                                        undetermined: position.term === 'Undetermined',
                                                    })}
                                                />
                                                {termStatuses[position.term]}
                                            </div>
                                            {Boolean(position.washSale) && (
                                                <>
                                                    <div
                                                        data-tooltip-id='washSale'
                                                        className='position-data__tag drop'
                                                    >
                                                        <MdOutlineWaterDrop />
                                                    </div>
                                                    <Tooltip
                                                        id={'washSale'}
                                                        place={'right'}
                                                        content={'Wash Sale'}
                                                        opacity={1}
                                                        style={{
                                                            backgroundColor: '#FFFFFF',
                                                            padding: 12,
                                                            borderRadius: 12,
                                                            fontSize: 12,
                                                            color: '#000000BF',
                                                            boxShadow: '0px 0px 6px -1px #0000001A',
                                                            maxWidth: '500px',
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <div
                                            className={classNames('position-data__info__item__title price', {
                                                positive: position.profitLoss > 0,
                                                negative: position.profitLoss < 0,
                                            })}
                                        >
                                            {position.profitLoss > 0 ? '+' : '-'} ${Math.abs(position.profitLoss)}
                                        </div>
                                    </div>
                                    <div className='position-data__info__item'>
                                        <span className='position-data__info__item__key'>Quantity:</span>
                                        <ChangedValueItem
                                            oldValue={position.oldQty}
                                            newValue={position.newQty}
                                            change={position.change}
                                        />
                                    </div>
                                    <div className='position-data__info__item'>
                                        <span className='position-data__info__item__key'>Date:</span>
                                        {moment(position.executionDate).format(formats.fullTime)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {affectedPositions?.closed && (
                    <div className='affected-positions__position'>
                        <div className='title'>
                            <IoCloseOutline /> <h3> Closed Positions</h3>
                        </div>
                        {affectedPositions.closed.map((position) => (
                            <div className='position-data'>
                                <div className='position-data__info'>
                                    <div className='position-data__info__item'>
                                        <div className='position-data__info__item__title'>
                                            <span className='position-data__info__item__value title'>
                                                {position.id}
                                            </span>
                                            <div className='position-data__tag term'>
                                                <div
                                                    className={classNames('circle', {
                                                        long: position.term === 'LongTerm',
                                                        short: position.term === 'ShortTerm',
                                                        undetermined: position.term === 'Undetermined',
                                                    })}
                                                />
                                                {termStatuses[position.term]}
                                            </div>
                                            {Boolean(position.washSale) && (
                                                <>
                                                    <div
                                                        data-tooltip-id='washSale'
                                                        className='position-data__tag drop'
                                                    >
                                                        <MdOutlineWaterDrop />
                                                    </div>
                                                    <Tooltip
                                                        id={'washSale'}
                                                        place={'right'}
                                                        content={'Wash Sale'}
                                                        opacity={1}
                                                        style={{
                                                            backgroundColor: '#FFFFFF',
                                                            padding: 12,
                                                            borderRadius: 12,
                                                            fontSize: 12,
                                                            color: '#000000BF',
                                                            boxShadow: '0px 0px 6px -1px #0000001A',
                                                            maxWidth: '500px',
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <div
                                            className={classNames('position-data__info__item__title price', {
                                                positive: position.profitLoss > 0,
                                                negative: position.profitLoss < 0,
                                            })}
                                        >
                                            {position.profitLoss > 0 ? '+' : '-'} ${Math.abs(position.profitLoss)}
                                        </div>
                                    </div>
                                    <div className='position-data__info__item'>
                                        <span className='position-data__info__item__key'>Quantity:</span>
                                        <ChangedValueItem
                                            oldValue={position.oldQty}
                                            newValue={position.newQty}
                                            change={position.change}
                                        />
                                    </div>
                                    <div className='position-data__info__item'>
                                        <span className='position-data__info__item__key'>Date:</span>
                                        {moment(position.executionDate).format(formats.fullTime)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Preloader>
    );
};
